<template>
  <div class="asr-create-pick-call tw-relative tw-min-w-[820px] tw-max-w-[100%]">
    <div class="tw-mb-3 tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
      <CustomInput
        :value="searchTerm"
        :isSearch="true"
        placeholder="Поиск (ЖК, проект, подрядчик, этаж)"
        v-debounce:300="onSearchChange"
        class="tw-min-w-[260px]"
      />
      <span class="tw-text-grey-7 tw-text-sm">
        Принятых вызовов: {{ filteredCalls.length }}
      </span>
    </div>

    <q-linear-progress v-if="isLoading" indeterminate />

    <div v-if="!isLoading && !filteredCalls.length" class="tw-py-8">
      <NotFoundText text="Принятых вызовов не найдено" />
    </div>

    <div
      v-if="!isLoading && filteredCalls.length"
      class="asr-create-pick-call__list tw-divide-y tw-divide-grey-4 tw-border tw-border-grey-4 tw-rounded"
    >
      <div
        v-for="row in filteredCalls"
        :key="row.help_call_id"
        class="asr-create-pick-call__row tw-flex tw-items-stretch tw-gap-3 tw-p-3"
      >
        <div class="tw-flex-1 tw-min-w-0">
          <div class="tw-mt-0.5 tw-text-sm tw-text-grey-8 tw-truncate">
            <span v-if="row.block_name">Блок {{ row.block_name }}</span>
            <span v-if="row.level_num != null">
              · Этаж {{ row.level_num }}
            </span>
            <span v-if="row.work_set_check_group_name">
              · {{ row.work_set_check_group_name }}
            </span>
            <span v-if="row.placement_type_name">
              · {{ row.placement_type_name }}
            </span>
          </div>
        </div>
        <div class="tw-flex tw-flex-col tw-items-end tw-gap-1">
          <q-btn
            unelevated
            no-caps
            color="primary"
            label="Сформировать АСР"
            :disable="!resolvedContractorId || row.asr_docs_remaining === 0"
            @click="onPickCall(row)"
          />
          <span
            v-if="row.asrLimitHint"
            class="tw-text-xs tw-text-grey-7 tw-italic tw-text-right tw-max-w-[220px]"
          >
            {{ row.asrLimitHint }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomInput from "@/components/form/CustomInput";
import NotFoundText from "@/components/common/NotFoundText";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { useAppStore } from "@/services/store/app";
import { getCheckedCallsForAsr } from "@/views/ResidentialSettings/services";
import { MODAL_NAMES, getAsrDocsMaxByProjectType, getAsrDocumentTitle } from "@/constants";

export default {
  name: "AsrCreatePickCallLegacy",
  components: { CustomInput, NotFoundText },
  props: {
    data: { type: Object, required: true },
    closeModal: { type: Function, required: true },
  },
  data() {
    return {
      isLoading: false,
      calls: [],
      projectContractorId: null,
      searchTerm: "",
    };
  },
  setup() {
    const { showErrorSnackbar, showSucceedSnackbar } = useCustomSnackbar();
    const appStore = useAppStore();
    return { showErrorSnackbar, showSucceedSnackbar, appStore };
  },
  computed: {
    filteredCalls() {
      const q = (this.searchTerm || "").toLowerCase().trim();
      if (!q) return this.calls;
      return this.calls.filter((r) =>
        [
          r.resident_name,
          r.project_name,
          r.contractor_name,
          r.block_name,
          r.work_set_check_group_name,
          r.placement_type_name,
          r.level_num,
        ]
          .filter((x) => x != null && x !== "")
          .map((x) => String(x).toLowerCase())
          .some((s) => s.includes(q))
      );
    },
    resolvedContractorId() {
      if (this.data?.contractorId != null && this.data?.contractorId !== "") {
        const fromData = Number(this.data.contractorId);
        return Number.isNaN(fromData) ? null : fromData;
      }
      if (
        this.projectContractorId != null &&
        this.projectContractorId !== ""
      ) {
        const id = Number(this.projectContractorId);
        return Number.isNaN(id) ? null : id;
      }
      return null;
    },
  },
  mounted() {
    this.loadCalls();
  },
  methods: {
    async loadCalls() {
      this.isLoading = true;
      const rows = await getCheckedCallsForAsr({
        project_id: this.data?.projectId,
        project_entrance_id: this.data?.projectEntranceId,
      });
      this.isLoading = false;
      const list = Array.isArray(rows) ? rows : [];
      const contractorRow = list.find(
        (r) => r?.contractor_id != null && r?.contractor_id !== ""
      );
      this.projectContractorId = contractorRow?.contractor_id ?? null;
      this.calls = list.map((row) => this.mapCallRow(row));
    },
    mapCallRow(row) {
      const docsCount = Number(row?.asr_docs_count ?? 0);
      const defaultMax = getAsrDocsMaxByProjectType(this.data?.projectTypeCode);
      const docsMax = Number.isFinite(Number(row?.asr_docs_max))
        ? Number(row.asr_docs_max)
        : defaultMax;
      const docsRemaining = Number.isFinite(Number(row?.asr_docs_remaining))
        ? Number(row.asr_docs_remaining)
        : Math.max(0, docsMax - docsCount);
      let asrLimitHint = null;
      if (docsMax > 1) {
        asrLimitHint = `Создано ${docsCount} из ${docsMax}. Можно ещё ${docsRemaining}`;
      } else if (row?.last_asr_date) {
        asrLimitHint = `По вызову уже есть АСР ${row.last_asr_date}`;
      }
      return {
        ...row,
        asr_docs_count: docsCount,
        asr_docs_max: docsMax,
        asr_docs_remaining: docsRemaining,
        asrLimitHint,
      };
    },
    onSearchChange(value) {
      this.searchTerm = value || "";
    },
    onPickCall(row) {
      if (!this.resolvedContractorId) {
        this.showErrorSnackbar("Подрядчик проекта не назначен");
        return;
      }
      if (row.asr_docs_remaining === 0) {
        this.showErrorSnackbar("Достигнут лимит создания актов для этой группы");
        return;
      }
      const onSuccess = this.data?.onSuccess;
      this.closeModal();
      this.appStore.showModal(MODAL_NAMES.AsrCreateOrReform, {
        title: getAsrDocumentTitle(
          this.data?.projectTypeCode,
          this.data?.projectTypeId
        ),
        mode: "create",
        helpCallId: row.help_call_id,
        contractorId: this.resolvedContractorId,
        residentId: this.data?.residentId,
        residentName: row.resident_name,
        contractorName: row.contractor_name,
        projectName: row.project_name,
        levelNum: row.level_num,
        blockName: row.block_name,
        documentParams: this.data?.documentParams || {},
        enableAsrFloorRange: false,
        onSuccess: (payload) => {
          if (typeof onSuccess === "function") onSuccess(payload);
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.asr-create-pick-call {
  &__list {
    max-height: 60vh;
    overflow-y: auto;
  }

  &__row {
    transition: background-color 0.15s ease;
    &:hover {
      background-color: #f5f7fa;
    }
  }
}
</style>
